<template>
  <v-dialog v-model="localVisible" max-width="1050px" persistent scrollable>
    <v-card>
      <v-card-title class="primary white--text">
        <div>
          <div>{{ sourceRound ? 'Capturar ronda de reemplazo' : 'Capturar ronda histórica' }}</div>
          <div class="text-caption">{{ playerName }}</div>
        </div>
        <v-spacer />
        <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert v-if="sourceRound" type="info" dense outlined>
          Se creará una ronda nueva en borrador. La ronda anulada se conserva en el historial de auditoría.
        </v-alert>
        <v-alert v-if="!configuration.length" type="warning" dense outlined>
          No hay canchas disponibles para capturar la ronda.
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.course"
                :items="configuration"
                item-text="course_name"
                item-value="course_id"
                label="Cancha"
                outlined dense
                :rules="requiredRules"
                @change="courseChanged"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field v-model="form.played_on" type="date" label="Fecha de juego" outlined dense :rules="requiredRules" />
            </v-col>
            <v-col cols="6" sm="3" md="1">
              <v-select v-model="form.holes_played" :items="holeCountOptions" label="Hoyos" outlined dense :rules="requiredRules" @change="roundShapeChanged" />
            </v-col>
            <v-col v-if="showSide" cols="6" sm="3" md="2">
              <v-select v-model="form.side" :items="sideOptions" label="Vuelta" outlined dense @change="roundShapeChanged" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="5">
              <v-select
                v-model="form.tee"
                :items="availableTees"
                item-text="name"
                item-value="id"
                label="Salida / tee"
                outlined dense
                :disabled="!form.course"
                :rules="requiredRules"
                :hint="teeHint"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="form.handicap_index_at_play"
                type="number"
                step="0.1"
                min="-10"
                max="54"
                label="Índice al jugar"
                outlined dense
                hint="Déjalo vacío si en esa fecha aún era NH"
                persistent-hint
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field :value="courseSummary" label="Configuración" outlined dense readonly />
            </v-col>
          </v-row>

          <v-alert v-if="configurationMessage" type="warning" dense outlined>{{ configurationMessage }}</v-alert>

          <v-simple-table v-if="selectedHoles.length" dense class="mb-4">
            <thead>
              <tr><th>Hoyo</th><th>Par</th><th>Índice</th><th style="width: 160px">Golpes</th></tr>
            </thead>
            <tbody>
              <tr v-for="hole in selectedHoles" :key="hole.hole_number">
                <td class="font-weight-bold">{{ hole.hole_number }}</td>
                <td>{{ hole.par }}</td>
                <td>{{ holeIndex(hole) }}</td>
                <td>
                  <v-text-field
                    v-model.number="scores[hole.hole_number]"
                    type="number"
                    min="1"
                    max="30"
                    dense outlined hide-details
                    class="my-1"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr><th colspan="3" class="text-right">Total</th><th>{{ totalScore }}</th></tr>
            </tfoot>
          </v-simple-table>

          <v-textarea v-model="form.notes" label="Notas de la ronda" outlined rows="2" counter="500" />
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <span class="text-caption grey--text">La ronda se guardará como borrador y deberá validarse por separado.</span>
        <v-spacer />
        <v-btn text @click="$emit('close')">Cancelar</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="save">
          <v-icon left>mdi-content-save</v-icon>Guardar borrador
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'HandicapRoundDialog',
  props: {
    visible: { type: Boolean, default: false },
    player: { type: Object, default: null },
    configuration: { type: Array, default: () => [] },
    sourceRound: { type: Object, default: null },
    saving: { type: Boolean, default: false }
  },
  data () {
    return {
      valid: false,
      scores: {},
      form: this.emptyForm(),
      requiredRules: [value => Boolean(value) || 'Campo requerido'],
      sideOptions: [
        { text: 'Primera vuelta', value: 'first' },
        { text: 'Segunda vuelta', value: 'second' }
      ]
    }
  },
  computed: {
    localVisible: {
      get () { return this.visible },
      set (value) { if (!value) this.$emit('close') }
    },
    playerName () { return this.player?.display_name || 'Socio' },
    selectedCourse () { return this.configuration.find(item => item.course_id === this.form.course) || null },
    holeCountOptions () {
      if (!this.selectedCourse) return [9, 18]
      return this.selectedCourse.total_holes === 9 ? [9] : [9, 18]
    },
    showSide () { return this.selectedCourse?.total_holes === 18 && this.form.holes_played === 9 },
    availableTees () {
      if (!this.selectedCourse) return []
      return this.selectedCourse.tees.filter(tee => {
        const matchesPlayer = !this.player?.gender || this.player.gender === 'unspecified' || tee.gender === 'any' || tee.gender === this.player.gender
        if (!matchesPlayer) return false
        if (this.form.holes_played === 18 || this.selectedCourse.total_holes === 9) return tee.ready_for_full_round
        return this.form.side === 'second' ? tee.ready_for_second_nine : tee.ready_for_first_nine
      })
    },
    selectedHoles () {
      const holes = this.selectedCourse?.holes || []
      if (this.form.holes_played === 18 || this.selectedCourse?.total_holes === 9) return holes
      if (this.form.side === 'second') return holes.filter(hole => hole.hole_number > 9)
      return holes.filter(hole => hole.hole_number <= 9)
    },
    teeHint () {
      if (!this.form.course) return 'Selecciona primero la cancha'
      if (this.availableTees.length) return 'Solo se muestran salidas con rating y slope completos para esta vuelta'
      return 'No hay salidas completas para esta vuelta'
    },
    configurationMessage () {
      if (!this.selectedCourse) return ''
      if (!this.selectedCourse.holes?.length) return 'La API no devolvió la configuración de hoyos. Actualiza el backend antes de capturar.'
      if (this.selectedHoles.length !== this.form.holes_played) return 'La cancha no tiene todos los hoyos requeridos configurados.'
      if (!this.availableTees.length) return 'Configura rating y slope de una salida para esta vuelta.'
      return ''
    },
    courseSummary () {
      if (!this.selectedCourse) return '--'
      const par = this.selectedHoles.reduce((sum, hole) => sum + Number(hole.par || 0), 0)
      return `${this.form.holes_played} hoyos · Par ${par}`
    },
    totalScore () {
      return this.selectedHoles.reduce((sum, hole) => sum + Number(this.scores[hole.hole_number] || 0), 0)
    },
    scoresComplete () {
      return this.selectedHoles.length === this.form.holes_played && this.selectedHoles.every(hole => {
        const value = Number(this.scores[hole.hole_number])
        return Number.isInteger(value) && value >= 1 && value <= 30
      })
    },
    canSave () {
      return this.valid && this.scoresComplete && !this.configurationMessage && !this.saving
    }
  },
  watch: {
    visible (value) { if (value) this.reset() },
    configuration () { if (this.visible) this.reset() }
  },
  methods: {
    emptyForm () {
      const today = new Date()
      today.setMinutes(today.getMinutes() - today.getTimezoneOffset())
      return {
        course: null,
        tee: null,
        played_on: today.toISOString().slice(0, 10),
        holes_played: 18,
        side: 'first',
        handicap_index_at_play: '',
        notes: ''
      }
    },
    reset () {
      this.form = this.emptyForm()
      this.scores = {}
      if (this.player?.current_handicap_index !== null && this.player?.current_handicap_index !== undefined) {
        this.form.handicap_index_at_play = this.player.current_handicap_index
      }
      if (this.sourceRound) {
        this.form.course = this.sourceRound.course
        this.form.tee = this.sourceRound.tee
        this.form.played_on = this.sourceRound.played_on
        this.form.holes_played = this.sourceRound.holes_played
        this.form.handicap_index_at_play = this.sourceRound.handicap_index_at_play ?? ''
        this.form.notes = `Reemplaza ronda anulada ${this.sourceRound.id}. ${this.sourceRound.notes || ''}`.trim()
        const numbers = (this.sourceRound.holes || []).map(hole => hole.hole_number)
        if (numbers.length && Math.min(...numbers) > 9) this.form.side = 'second'
        this.scores = Object.fromEntries((this.sourceRound.holes || []).map(hole => [hole.hole_number, hole.strokes]))
      } else if (this.configuration.length === 1) {
        this.form.course = this.configuration[0].course_id
        this.courseChanged()
      }
      this.$nextTick(() => this.$refs.form?.resetValidation())
    },
    courseChanged () {
      this.form.tee = null
      this.form.holes_played = this.selectedCourse?.total_holes === 9 ? 9 : 18
      this.form.side = 'first'
      this.scores = {}
    },
    roundShapeChanged () {
      if (!this.availableTees.some(tee => tee.id === this.form.tee)) this.form.tee = null
      const selectedNumbers = new Set(this.selectedHoles.map(hole => hole.hole_number))
      this.scores = Object.fromEntries(Object.entries(this.scores).filter(([number]) => selectedNumbers.has(Number(number))))
    },
    holeIndex (hole) {
      if (this.player?.gender === 'female' && hole.handicap_index_ladies) return hole.handicap_index_ladies
      return hole.handicap_index
    },
    save () {
      if (!this.$refs.form.validate() || !this.canSave) return
      const data = {
        player: this.player.id,
        course: this.form.course,
        tee: this.form.tee,
        played_on: this.form.played_on,
        holes_played: this.form.holes_played,
        notes: this.form.notes,
        holes: this.selectedHoles.map(hole => ({ hole_number: hole.hole_number, strokes: Number(this.scores[hole.hole_number]) }))
      }
      if (this.form.handicap_index_at_play !== '' && this.form.handicap_index_at_play !== null) {
        data.handicap_index_at_play = Number(this.form.handicap_index_at_play)
      }
      this.$emit('save', data)
    }
  }
}
</script>
